import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { protectSheetWithRandomPassword } from './ExcelUtils';

function getColLetter(colNum) {
    let temp = '';
    while (colNum > 0) {
        let mod = (colNum - 1) % 26;
        temp = String.fromCharCode(65 + mod) + temp;
        colNum = Math.floor((colNum - mod) / 26);
    }
    return temp;
}

export async function exportProductSummaryWithPercentage({ data, fileName = 'Product_Summary.xlsx', groupByField, sheetName, branchOrDistrictName, formattedDate, activeStatus,loginPlaceId}) {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(sheetName);

    const gnNames = data.map(d => d[groupByField]);
    const productNames = [...new Set(data.flatMap(d => d.productSummaryDtoList.map(p => p.productName.trim())))];

    const productMap = {};
    productNames.forEach(name => productMap[name] = {});

    data.forEach(gn => {
        gn.productSummaryDtoList.forEach(p => {
            const name = p.productName.trim();
            productMap[name][gn[groupByField]] = {
                count: p.count,
                amount: p.amount
            };
        });
    });

    // Styles
    const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } },
        alignment: { vertical: 'middle', horizontal: 'center' },
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };
    const cellBorder = {
        border: {
            top: { style: 'thin' }, bottom: { style: 'thin' },
            left: { style: 'thin' }, right: { style: 'thin' }
        }
    };


   if (branchOrDistrictName) {
        const labelRow = sheet.addRow([` ${branchOrDistrictName} | Date: ${formattedDate}`]);
        labelRow.font = { bold: true };
        labelRow.getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E2EFDA' } // Light green background
        };
    }

    // Add Row 2: Selected branch or GN count
    const groupCount = [...new Set(gnNames)].length;
    const labelName =
        groupByField === 'branchName'
            ? 'branch'
            : groupByField === 'gnName'
                ? 'GN division'
                : groupByField;
    const countLabel = `Selected ${labelName} count: ${groupCount}`;
    const countRow = sheet.addRow([countLabel]);
    countRow.font = { italic: true, bold: true };
    countRow.getCell(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF2CC' } // light yellow
    };
    
    // Add Row 3: Account status label (e.g., "Active Accounts")
    if (activeStatus) {
        const labelRow = sheet.addRow([`${activeStatus}`]);
        labelRow.font = { bold: true };
        labelRow.getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'E2EFDE' } // Light green background
        };
    }
    

    // Header rows
    const row1 = ['productName'];
    const row2 = [''];
    gnNames.forEach(() => {
        row1.push('', '', '');
        row2.push('count', 'amount', 'percentage');
    });
    row1.push('', '', '');
    row2.push('count', 'amount', 'percentage');

    sheet.addRow(row1);
    sheet.addRow(row2);



    sheet.mergeCells('A4:A5');
    const productHeaderCell = sheet.getCell('A4');
    productHeaderCell.value = 'productName';
    productHeaderCell.alignment = { vertical: 'middle', horizontal: 'center' };
    productHeaderCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'D9D9D9' } // Light gray, change as needed
    };
    productHeaderCell.font = { bold: true };
    productHeaderCell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' }
    };
    // Merge headers and style
    let col = 2;
    gnNames.forEach(name => {
        sheet.mergeCells(4, col, 4, col + 2);
        const cell = sheet.getCell(4, col);
        cell.value = name;
        Object.assign(cell, headerStyle);
        col += 3;
    });
    sheet.mergeCells(4, col, 4, col + 2);
    const grandCell = sheet.getCell(4, col);
    grandCell.value = 'Grand total';
    Object.assign(grandCell, headerStyle);

    const headerRow2 = sheet.getRow(5);
    headerRow2.eachCell(cell => Object.assign(cell, headerStyle));
    

    // Adjust data start row
    const firstDataRow = 6;

    // Add data rows
    const productRows = [];
    productNames.forEach((name) => {
        const rowValues = [name];
        gnNames.forEach(gn => {
            const entry = productMap[name][gn] || { count: 0, amount: 0 };
            rowValues.push(entry.count, entry.amount, null); // percentage placeholder
        });

        let totalCount = 0;
        let totalAmount = 0;
        gnNames.forEach(gn => {
            const entry = productMap[name][gn] || { count: 0, amount: 0 };
            totalCount += entry.count;
            totalAmount += entry.amount;
        });
        rowValues.push(totalCount, totalAmount, null);
        const addedRow = sheet.addRow(rowValues);
        productRows.push(addedRow);

        // Style cells
        addedRow.eachCell((cell, colNum) => {
            if (colNum === 1) return;
            const mod = (colNum - 2) % 3;
            if (mod === 0) cell.numFmt = '#,##0';       // count
            else if (mod === 1) cell.numFmt = '#,##0.00';  // amount
            else if (mod === 2) cell.numFmt = '0%';        // percentage
            Object.assign(cell, cellBorder);
        });

        // Alternate row shading
        if ((addedRow.number - 2) % 2 === 0) {
            addedRow.eachCell((cell, colNum) => {
                if (colNum > 1) {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'F2F2F2' }
                    };
                }
            });
        }
    });

    // const firstDataRow = 3;
    const lastDataRow = sheet.lastRow.number;

    // Total row using formulas
    const totalRowValues = ['Total'];
    const totalColCount = 1 + (gnNames.length + 1) * 3;
    for (let colIdx = 2; colIdx <= totalColCount; colIdx++) {
        const mod = (colIdx - 2) % 3;
        if (mod === 0 || mod === 1) {
            const colLetter = getColLetter(colIdx);
            totalRowValues.push({
                formula: `SUM(${colLetter}${firstDataRow}:${colLetter}${lastDataRow})`,
                result: 0
            });
        } else {
            totalRowValues.push(null); // skip percentage in totals
        }
    }

    const totalRow = sheet.addRow(totalRowValues);
    totalRow.font = { bold: true };
    totalRow.eachCell((cell, colNum) => {
        if (colNum === 1) return;
        const mod = (colNum - 2) % 3;
        if (mod === 0) cell.numFmt = '#,##0';
        else if (mod === 1) cell.numFmt = '#,##0.00';
        Object.assign(cell, cellBorder);
    });

    // Set percentage formulas after total row is available
    productRows.forEach(row => {
        const rowNum = row.number;
        gnNames.forEach((_, gIdx) => {
            const percentCol = 4 + gIdx * 3;
            const amountCol = 3 + gIdx * 3;

            const amountCell = `${getColLetter(amountCol)}${rowNum}`;
            const totalAmountCell = `${getColLetter(amountCol)}${totalRow.number}`;

            const cell = row.getCell(percentCol);
            cell.value = { formula: `${amountCell}/${totalAmountCell}`, result: 0 };
            cell.numFmt = '0%';
        });

        const grandPercentCol = sheet.columnCount;
        const grandAmountCol = grandPercentCol - 1;

        const grandAmountCell = `${getColLetter(grandAmountCol)}${rowNum}`;
        const grandTotalAmountCell = `${getColLetter(grandAmountCol)}${totalRow.number}`;

        const grandPercentCell = row.getCell(grandPercentCol);
        grandPercentCell.value = { formula: `${grandAmountCell}/${grandTotalAmountCell}`, result: 0 };
        grandPercentCell.numFmt = '0%';
    });

    // Column widths
    sheet.getColumn(1).width = 50;
    for (let i = 2; i <= sheet.columnCount; i++) {
        sheet.getColumn(i).width = 15;
    }

     if(loginPlaceId==="1"){
        const password = protectSheetWithRandomPassword(sheet,12);
        console.log(`Sheet protected with password: ${password}`);
      }
    
    // Freeze top two rows and first column
    sheet.views = [{ state: 'frozen', xSplit: 1, ySplit: 5 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, fileName);
}
